import { api, type BillingInfo, type BillingSettings, type BillingView, type Currency, type PaidPlan, type PaymentRequest, type Period } from './api';
import { esc, fmtDate, openModal, qs, toast } from './ui';
import { getLang, t as $t, tk } from '@enercad/core';

/* Plans and payment by bank transfer: the company admin's request, the platform admin's prices and requests. */

const PLANS: PaidPlan[] = ['basic', 'pro', 'enterprise'];
const PLAN_NAME: Record<PaidPlan, string> = { basic: tk('Temel'), pro: tk('Profesyonel'), enterprise: tk('Kurumsal') };
const CURRENCIES: Currency[] = ['TRY', 'EUR'];

export const money = (n: number, c: Currency) => {
  try {
    return new Intl.NumberFormat(getLang(), { style: 'currency', currency: c }).format(n);
  } catch {
    return `${n} ${c}`;
  }
};
const perName = (p: Period) => (p === 'year' ? $t('yıllık') : $t('aylık'));
const summary = (r: PaymentRequest) => `${$t(PLAN_NAME[r.plan])} · ${perName(r.period)} · ${money(r.amount, r.currency)}`;

function billingLines(b: BillingInfo): string {
  const rows = [`${b.type === 'company' ? $t('Kurumsal') : $t('Bireysel')} · ${esc(b.abroad ? b.country : 'Türkiye')}`, `<b>${esc(b.name)}</b>`];
  if (b.idNo) rows.push(`${$t('T.C. kimlik no')}: ${esc(b.idNo)}`);
  if (b.taxNo) rows.push(`${$t('Vergi dairesi')}: ${esc(b.taxOffice)} · ${$t('Vergi no')}: ${esc(b.taxNo)}`);
  if (b.vatId) rows.push(`${$t('VAT numarası')}: ${esc(b.vatId)}`);
  rows.push(esc(b.address), `${$t('Fatura e-postası')}: ${esc(b.email)}`);
  return rows.join('<br>');
}

/** Transfer details of an open request: reference, amount, bank. */
function transferBox(r: PaymentRequest, bank: string, note: string) {
  return `<div class="paybox">
    <div class="payrow"><span><span class="note">${$t('Referans kodu')}</span><b class="payref">${esc(r.ref)}</b></span><span><span class="note">${$t('Tutar')}</span><b>${esc(money(r.amount, r.currency))}</b></span></div>
    <span class="note">${esc(summary(r))}</span>
    <pre class="bank">${esc(bank)}</pre>
    <p>${$t('Havale / EFT açıklamasına referans kodunu yazın. Ödemeniz ulaştığında planınız etkinleşir ve e-posta ile bildirilir.')}</p>
    ${note ? `<p class="note">${esc(note)}</p>` : ''}
  </div>`;
}

/** Company admin: choose a plan, enter the invoice details, get the transfer details. */
export async function paymentDialog(onDone?: () => void) {
  let v: BillingView;
  try {
    v = await api.billing();
  } catch (e) {
    return void toast((e as Error).message);
  }
  const priced = (cur: Currency, per: Period) => v.plans.some((p) => p.prices[per][cur]);
  const st = {
    period: 'month' as Period,
    currency: (getLang() === 'tr' ? 'TRY' : 'EUR') as Currency,
    plan: '' as PaidPlan | '',
    b: { type: 'company', abroad: getLang() !== 'tr', country: '', name: v.tenant.name, idNo: '', taxOffice: '', taxNo: '', vatId: '', address: '', email: '', ...(v.billing ?? {}) } as BillingInfo,
  };
  if (!priced(st.currency, st.period)) st.currency = CURRENCIES.find((c) => priced(c, 'month') || priced(c, 'year')) ?? st.currency;
  if (!priced(st.currency, st.period) && priced(st.currency, 'year')) st.period = 'year';
  const pickPlan = () => {
    const ok = (k: PaidPlan) => !!v.plans.find((p) => p.key === k)?.prices[st.period][st.currency];
    if (!st.plan || !ok(st.plan)) st.plan = ok(v.tenant.plan as PaidPlan) ? (v.tenant.plan as PaidPlan) : (PLANS.find(ok) ?? '');
  };
  pickPlan();

  const plansHtml = () =>
    v.plans
      .map((p) => {
        const price = p.prices[st.period][st.currency];
        const lim = $t('{0} kullanıcı · {1} proje', p.users ?? '∞', p.projects ?? '∞');
        return `<label class="planopt${price ? '' : ' off'}"><input type="radio" name="plan" value="${p.key}"${st.plan === p.key ? ' checked' : ''}${price ? '' : ' disabled'}>
          <span><b>${esc(p.name)}</b><span class="note">${esc(lim)}</span></span>
          <b>${price ? `${esc(money(price, st.currency))} <span class="note">/ ${st.period === 'year' ? $t('yıl') : $t('ay')}</span>` : `<span class="note">${$t('fiyat için bizimle iletişime geçin')}</span>`}</b></label>`;
      })
      .join('');
  const fld = (id: string, label: string, val: string, attrs = '') => `<label class="fld"><span>${label}</span><input id="${id}" value="${esc(val)}" ${attrs}></label>`;
  const fieldsHtml = () => {
    const b = st.b, tr = !b.abroad;
    return (
      fld('bname', b.type === 'company' ? $t('Firma ünvanı') : $t('Ad soyad'), b.name, 'autocomplete="off"') +
      (tr && b.type === 'individual' ? fld('bid', $t('T.C. kimlik no'), b.idNo, 'inputmode="numeric" maxlength="11"') : '') +
      (tr && b.type === 'company' ? `<div class="row2">${fld('btaxo', $t('Vergi dairesi'), b.taxOffice)}${fld('btaxn', $t('Vergi no'), b.taxNo, 'inputmode="numeric" maxlength="11"')}</div>` : '') +
      (!tr ? `<div class="row2">${fld('bcountry', $t('Ülke'), b.country)}${fld('bvat', $t('VAT numarası (varsa)'), b.vatId)}</div>` : '') +
      `<label class="fld"><span>${$t('Adres')}</span><textarea id="baddr" class="addr" rows="2">${esc(b.address)}</textarea></label>` +
      fld('bmail', $t('Fatura e-postası'), b.email, `type="email" placeholder="${$t('Boş bırakılırsa sizin e-postanız')}" autocapitalize="none" spellcheck="false"`)
    );
  };
  const t = v.tenant;
  const body = () => `<h2>${$t('Plan ve ödeme')}</h2>
    <p>${$t('Şu anki plan: <b>{0}</b> · bitiş: {1}', esc(t.planName), t.expires ? esc(t.expires) : $t('süresiz'))}</p>
    ${v.open ? `<h3 class="payh">${$t('Bekleyen ödemeniz')}</h3>${transferBox(v.open, v.bank, v.note)}<div class="btnrow" style="margin:-4px 0 12px"><button class="btn danger" data-m="cancelreq">${$t('Talebi iptal et')}</button></div><h3 class="payh">${$t('Yeni talep')}</h3>` : ''}
    ${!v.bank.trim() || !v.plans.some((p) => CURRENCIES.some((c) => p.prices.month[c] || p.prices.year[c]))
      ? `<p class="note">${$t('Online ödeme bilgileri henüz tanımlanmadı. Plan değişikliği için bizimle iletişime geçin.')}</p><div class="acts"><button class="btn" data-m="close">${$t('Kapat')}</button></div>`
      : `<div class="row2"><label class="fld"><span>${$t('Süre')}</span><select id="pper"><option value="month"${st.period === 'month' ? ' selected' : ''}>${$t('Aylık')}</option><option value="year"${st.period === 'year' ? ' selected' : ''}>${$t('Yıllık')}</option></select></label>
      <label class="fld"><span>${$t('Para birimi')}</span><select id="pcur">${CURRENCIES.map((c) => `<option value="${c}"${st.currency === c ? ' selected' : ''}>${c === 'TRY' ? 'TL (₺)' : 'EUR (€)'}</option>`).join('')}</select></label></div>
      <div class="planopts" id="pplans">${plansHtml()}</div>
      ${v.note ? `<p class="note">${esc(v.note)}</p>` : ''}
      <h3 class="payh">${$t('Fatura bilgileri')}</h3>
      <div class="row2"><label class="fld"><span>${$t('Fatura yeri')}</span><select id="bwhere"><option value="tr"${st.b.abroad ? '' : ' selected'}>Türkiye</option><option value="abroad"${st.b.abroad ? ' selected' : ''}>${$t('Yurt dışı')}</option></select></label>
      <label class="fld"><span>${$t('Fatura türü')}</span><select id="btype"><option value="individual"${st.b.type === 'individual' ? ' selected' : ''}>${$t('Bireysel')}</option><option value="company"${st.b.type === 'company' ? ' selected' : ''}>${$t('Kurumsal')}</option></select></label></div>
      <div id="bfields">${fieldsHtml()}</div>
      <div class="err" id="perr"></div>
      <div class="acts"><button class="btn" data-m="close">${$t('Kapat')}</button><button class="btn pri" data-m="send">${$t('Ödeme talebi gönder')}</button></div>`}`;

  const read = (dlg: HTMLElement) => {
    const val = (id: string) => dlg.querySelector<HTMLInputElement | HTMLTextAreaElement>('#' + id)?.value.trim();
    const b = st.b;
    b.name = val('bname') ?? b.name;
    b.idNo = val('bid') ?? b.idNo;
    b.taxOffice = val('btaxo') ?? b.taxOffice;
    b.taxNo = val('btaxn') ?? b.taxNo;
    b.country = val('bcountry') ?? b.country;
    b.vatId = val('bvat') ?? b.vatId;
    b.address = val('baddr') ?? b.address;
    b.email = val('bmail') ?? b.email;
  };

  const close = openModal(body(), async (a, dlg) => {
    if (a === 'cancelreq' && v.open) {
      try {
        await api.cancelBillingRequest(v.open.id);
      } catch (e) {
        return void toast((e as Error).message);
      }
      close();
      toast($t('Ödeme talebi iptal edildi'));
      onDone?.();
      return void paymentDialog(onDone);
    }
    if (a !== 'send') return;
    read(dlg);
    const err = qs(dlg, '#perr');
    if (!st.plan) return void (err.textContent = $t('Bir plan seçin.'));
    try {
      const r = await api.billingRequest({ plan: st.plan, period: st.period, currency: st.currency, billing: st.b });
      dlg.innerHTML = `<h2>${$t('Ödeme talebiniz alındı')}</h2>${transferBox(r.request, r.bank, r.note)}
        <p class="note">${$t('Bu bilgiler e-postanıza da gönderildi.')}</p>
        <div class="acts"><button class="btn pri" data-m="close">${$t('Kapat')}</button></div>`;
      onDone?.();
    } catch (e) {
      err.textContent = (e as Error).message;
    }
  });
  const dlg = document.querySelector<HTMLElement>('.modal:last-child .dlg')!;
  dlg.addEventListener('change', (e) => {
    const el = e.target as HTMLInputElement;
    if (el.name === 'plan') st.plan = el.value as PaidPlan;
    else if (el.id === 'pper' || el.id === 'pcur') {
      st.period = qs<HTMLSelectElement>(dlg, '#pper').value as Period;
      st.currency = qs<HTMLSelectElement>(dlg, '#pcur').value as Currency;
      pickPlan();
      qs(dlg, '#pplans').innerHTML = plansHtml();
    } else if (el.id === 'bwhere' || el.id === 'btype') {
      read(dlg);
      st.b.abroad = qs<HTMLSelectElement>(dlg, '#bwhere').value === 'abroad';
      st.b.type = qs<HTMLSelectElement>(dlg, '#btype').value as BillingInfo['type'];
      qs(dlg, '#bfields').innerHTML = fieldsHtml();
    }
  });
}

/** Platform admin: plan prices and the bank details shown to customers. */
export async function billingSettingsDialog() {
  let s: BillingSettings;
  try {
    s = (await api.platformBilling()).settings;
  } catch (e) {
    return void toast((e as Error).message);
  }
  const cell = (p: PaidPlan, per: Period, c: Currency) => `<td><input class="m" data-price="${p}.${per}.${c}" inputmode="decimal" value="${s.prices[p][per][c] ?? ''}" aria-label="${esc(`${$t(PLAN_NAME[p])} ${perName(per)} ${c}`)}"></td>`;
  const close = openModal(
    `<h2>${$t('Planlar ve ödeme')}</h2>
    <p class="note">${$t('Firmalar bu fiyatları "Firma ve kullanıcılar → Plan ve ödeme" ekranında görür ve havale / EFT ile ödeme talebi gönderir. Boş bırakılan fiyat için "bizimle iletişime geçin" yazar.')}</p>
    <div class="tblw"><table class="t pricet"><thead><tr><th>${$t('Plan')}</th><th>${$t('Aylık')} TL</th><th>${$t('Aylık')} EUR</th><th>${$t('Yıllık')} TL</th><th>${$t('Yıllık')} EUR</th></tr></thead><tbody>
      ${PLANS.map((p) => `<tr><td><b>${$t(PLAN_NAME[p])}</b></td>${cell(p, 'month', 'TRY')}${cell(p, 'month', 'EUR')}${cell(p, 'year', 'TRY')}${cell(p, 'year', 'EUR')}</tr>`).join('')}
    </tbody></table></div>
    <label class="fld"><span>${$t('Fiyat notu (ör. KDV dahil / hariç)')}</span><input id="bnote" value="${esc(s.note)}"></label>
    <label class="fld"><span>${$t('Havale / EFT bilgileri')}</span><textarea id="bbank" class="addr" rows="4" placeholder="${$t('Banka, şube, hesap sahibi, IBAN (TL ve EUR)')}">${esc(s.bank)}</textarea></label>
    <div class="err" id="berr"></div>
    <div class="acts"><button class="btn" data-m="close">${$t('Kapat')}</button><button class="btn pri" data-m="save">${$t('Kaydet')}</button></div>`,
    async (a, dlg) => {
      if (a !== 'save') return;
      const prices = Object.fromEntries(PLANS.map((p) => [p, { month: {}, year: {} }])) as BillingSettings['prices'];
      for (const el of dlg.querySelectorAll<HTMLInputElement>('[data-price]')) {
        const [p, per, c] = el.dataset.price!.split('.') as [PaidPlan, Period, Currency];
        const raw = el.value.trim().replace(/\s/g, '').replace(',', '.');
        if (raw) prices[p][per][c] = Number(raw);
      }
      try {
        await api.savePlatformBilling({ prices, bank: qs<HTMLTextAreaElement>(dlg, '#bbank').value, note: qs<HTMLInputElement>(dlg, '#bnote').value });
        close();
        toast($t('Plan fiyatları kaydedildi'));
      } catch (e) {
        qs(dlg, '#berr').textContent = (e as Error).message;
      }
    },
  );
}

/** Platform admin: payment requests; marking one paid sets the plan and extends the paid-until date. */
export async function paymentsDialog(onChange?: () => void) {
  let list: PaymentRequest[];
  try {
    list = (await api.platformPayments()).requests;
  } catch (e) {
    return void toast((e as Error).message);
  }
  const status = (r: PaymentRequest) =>
    r.status === 'open' ? `<span class="pill warn">${$t('bekliyor')}</span>`
    : r.status === 'paid' ? `<span class="pill ok">${$t('ödendi')}</span> <span class="note">${$t('bitiş {0}', esc(r.paid_until ?? ''))}</span>`
    : `<span class="pill">${$t('iptal')}</span>`;
  const close = openModal(
    `<h2>${$t('Ödeme talepleri')}</h2>
    ${list.length ? `<div class="vlist">${list
      .map((r, i) => `<div class="payreq${r.status === 'open' ? '' : ' done'}">
        <div class="payrow"><span><b class="payref">${esc(r.ref)}</b> ${status(r)}</span><span class="note">${esc(fmtDate(r.created_at))}</span></div>
        <div><b>${esc(r.tenant_name ?? '')}</b> · ${esc(summary(r))}</div>
        <div class="note">${$t('Talep eden: {0}', esc(`${r.user_name ?? ''} <${r.user_email ?? ''}>`))}</div>
        <details><summary class="note">${$t('Fatura bilgileri')}</summary><div class="billto">${billingLines(r.billing)}</div></details>
        ${r.status === 'open' ? `<div class="btnrow"><button class="btn pri" data-m="paid${i}">${$t('Ödendi')}</button><button class="btn" data-m="cancel${i}">${$t('İptal et')}</button></div>` : ''}
      </div>`)
      .join('')}</div>` : `<p class="note">${$t('Henüz ödeme talebi yok.')}</p>`}
    <div class="acts"><button class="btn" data-m="close">${$t('Kapat')}</button></div>`,
    async (a) => {
      const m = /^(paid|cancel)(\d+)$/.exec(a);
      const r = m && list[+m[2]];
      if (!m || !r) return;
      if (m[1] === 'paid' && !confirm($t('{0} için {1} ödemesi alındı mı? Plan "{2}" olur ve bitiş tarihi uzatılır.', r.tenant_name ?? '', money(r.amount, r.currency), $t(PLAN_NAME[r.plan])))) return;
      try {
        if (m[1] === 'paid') {
          const res = await api.platformPaid(r.id);
          toast($t('{0}: ödendi, bitiş {1}', r.tenant_name ?? '', res.paid_until));
        } else {
          await api.platformCancelPayment(r.id);
          toast($t('Ödeme talebi iptal edildi'));
        }
      } catch (e) {
        return void toast((e as Error).message);
      }
      close();
      onChange?.();
      void paymentsDialog(onChange);
    },
  );
}
